import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ContactFormData } from '../../../schemas/contact.schema';
import { Contact, HandoffNote } from '../../../types/contact.types';
import { updateContact, optOutContact as optOutContactApi, createContactNote, deleteContactNote, updateContactNote } from '../../../api/remiApi';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT } from '../../../lib/graphql/queries/contacts';

export const useContactMutations = (_refetchCallback?: () => void) => {
  const { organization } = useOrganizationStore();
  const apolloClient = useApolloClient();

  // Refetch every cached GET_CONTACT observer EXCEPT zombie ones mounted
  // with an empty contactId (e.g., modals that mounted before their id resolved).
  // Apollo's refetchQueries ignores `skip`, so without this guard every save
  // fires a spurious `POST GetContact` with contactId: "".
  const refetchContacts = useCallback(
    () =>
      apolloClient.refetchQueries({
        include: [GET_CONTACT],
        onQueryUpdated(observableQuery) {
          const vars = observableQuery.options.variables as
            | { contactId?: string }
            | undefined;
          return !!vars?.contactId;
        },
      }),
    [apolloClient]
  );

  const editContact = useCallback(
    async (id: string, data: ContactFormData): Promise<void> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await updateContact(id, orgId, data as Record<string, unknown>);
      await refetchContacts();
    },
    [organization?.id, refetchContacts]
  );

  const updateContactTags = useCallback(
    async (contactId: string, newTags: string[]): Promise<void> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await updateContact(contactId, orgId, { tags: newTags });
      await refetchContacts();
    },
    [organization?.id, refetchContacts]
  );

  const optOutContact = useCallback(
    async (contactId: string): Promise<void> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await optOutContactApi(contactId, orgId);
      await refetchContacts();
    },
    [organization?.id, refetchContacts]
  );

  // Not implemented — use useContact hook (Apollo GET_CONTACT) for reads
  const getContactById = useCallback(async (_contactId: string): Promise<Contact> => {
    throw new Error('Not implemented — use useContact hook instead');
  }, []);

  const addHandoffNote = useCallback(
    async (
      contactId: string,
      note: Omit<HandoffNote, 'timestamp'>
    ): Promise<HandoffNote> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await createContactNote(contactId, orgId, {
        body: note.note,
        authorId: note.addedBy,
        authorName: note.addedByName,
      });
      await refetchContacts();
      return { ...note, timestamp: new Date().toISOString() };
    },
    [organization?.id, refetchContacts]
  );

  const deleteHandoffNote = useCallback(
    async (
      contactId: string,
      noteId: string
    ): Promise<void> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await deleteContactNote(contactId, noteId, orgId);
      await refetchContacts();
    },
    [organization?.id, refetchContacts]
  );

  const updateHandoffNote = useCallback(
    async (
      contactId: string,
      noteId: string,
      body: string
    ): Promise<void> => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization selected');
      await updateContactNote(contactId, noteId, orgId, { body });
      await refetchContacts();
    },
    [organization?.id, refetchContacts]
  );

  return {
    editContact,
    updateContactTags,
    optOutContact,
    getContactById,
    addHandoffNote,
    deleteHandoffNote,
    updateHandoffNote,
  };
};

// Re-export as useContacts for backward-compat with old import paths
export { useContactMutations as useContacts };
